import { IFileItem } from '../../createAndEdit/types';
import { downloadBudgetAttachment } from '../../../../api/fetchBudget';
import moment from 'moment';

interface IDetailFilesProps {
  files: IFileItem[];
  budgetId?: string;
}

const DetailFiles = (props: IDetailFilesProps) => {
  const formatFileSize = (size?: string | number): string => {
    if (!size) return 'Tamanho não disponível';
    const sizeNum = typeof size === 'string' ? Number.parseFloat(size) : size;
    if (sizeNum < 1024) return `${sizeNum} B`;
    if (sizeNum < 1024 * 1024) return `${(sizeNum / 1024).toFixed(1)} KB`;
    return `${(sizeNum / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileThumbnail = (file: IFileItem): string => {
    if (!file.type) return '/assets/images/file-icon.svg';
    
    const type = file.type.toLowerCase();
    if (type.startsWith('image/')) {
      return file.link || '/assets/images/file-icon.svg';
    } else if (type === 'application/pdf') {
      return '/assets/images/pdf-icon.svg';
    } else if (type.startsWith('video/')) {
      return '/assets/images/video-icon.svg';
    } else if (
      type === 'application/zip' ||
      type === 'application/x-rar-compressed'
    ) {
      return '/assets/images/zip-icon.svg';
    } else {
      return '/assets/images/file-icon.svg';
    }
  };

  const isPdfFile = (file: IFileItem): boolean => {
    const fileType = file.type?.toLowerCase() || '';
    return fileType === 'application/pdf';
  };

  const isSpreadsheetFile = (file: IFileItem): boolean => {
    const fileType = file.type?.toLowerCase() || '';
    return (
      fileType === 'application/vnd.ms-excel' ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
      fileType === 'application/vnd.oasis.opendocument.spreadsheet' ||
      fileType === 'text/csv' ||
      file.name?.toLowerCase().endsWith('.xls') ||
      file.name?.toLowerCase().endsWith('.xlsx') ||
      file.name?.toLowerCase().endsWith('.ods') ||
      file.name?.toLowerCase().endsWith('.csv')
    );
  };

  const handleDownload = async (file: IFileItem) => {
    if (file.link) {
      const link = document.createElement('a');
      link.href = file.link;
      link.setAttribute('download', file.name || '');
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      return;
    }

    if (file.file) {
      const url = URL.createObjectURL(file.file);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name || '');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      URL.revokeObjectURL(url);
      return;
    }

    if (!props.budgetId || !file.name) return;

    try {
      const response = await downloadBudgetAttachment(props.budgetId, file.name);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.name);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[DetailFiles] Erro ao baixar arquivo:', error);
    }
  };

  return (
    <div className="mb-6 px-6">
      <div className="font-bold mb-4">Anexos</div>
      {props.files.length === 0 ? (
        <div className="text-gray-500 italic">Nenhum arquivo</div>
      ) : (
        <div className="flex flex-col gap-4">
        {props.files.map((file, index) => (
          <div
            key={`file-${file.name}-${index}`}
            className="border border-gray-200 rounded-md p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex">
              <div className="thumbnail mr-4 flex justify-center items-center w-[200px] h-[120px] border border-gray-200 rounded bg-gray-50 relative overflow-hidden flex-shrink-0">
                {isPdfFile(file) && file.link ? (
                  <div className="w-full h-full relative">
                    <iframe
                      src={`${file.link}#page=1&zoom=page-fit`}
                      className="w-full h-full border-0"
                      title={`Preview ${file.name}`}
                      style={{ pointerEvents: 'none' }}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-size-10 px-2 py-1 flex items-center justify-center">
                      <span className="mr-1">📄</span>
                      <span>PDF</span>
                    </div>
                  </div>
                ) : isPdfFile(file) ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-red-50 to-red-100 p-4">
                    <div className="text-5xl mb-2">📄</div>
                    <div className="text-size-12 text-gray-700 text-center font-semibold">PDF</div>
                    <div className="text-size-10 text-gray-600 text-center mt-1 truncate w-full">{file.name}</div>
                  </div>
                ) : isSpreadsheetFile(file) ? (
                  <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-green-50 to-blue-50 p-4">
                    <div className="text-4xl mb-2">📊</div>
                    <div className="text-size-10 text-gray-600 text-center font-semibold">
                      {file.name?.toLowerCase().endsWith('.csv') ? 'CSV' : 'Planilha'}
                    </div>
                  </div>
                ) : (
                  <img
                    src={getFileThumbnail(file)}
                    alt={file.name}
                    style={{
                      width: '200px',
                      height:
                        (file.type?.toLowerCase()?.includes('png') ||
                        file.type?.toLowerCase()?.includes('jpeg') ||
                        file.type?.toLowerCase()?.includes('jpg'))
                          ? '120px'
                          : '65px',
                      objectFit:
                        (file.type?.toLowerCase()?.includes('png') ||
                        file.type?.toLowerCase()?.includes('jpeg') ||
                        file.type?.toLowerCase()?.includes('jpg'))
                          ? 'cover'
                          : 'contain',
                    }}
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = '/assets/images/file-icon.svg';
                    }}
                  />
                )}
              </div>
              <div className="flex justify-between w-full">
                <div className="flex-1">
                  <div className="font-bold mb-1">{file.name}</div>
                  {file.type && <div className="text-size-12 text-gray-600">tipo: {file.type}</div>}
                  {file.size && <div className="text-size-12 text-gray-600">tamanho: {formatFileSize(file.size)}</div>}
                  {file.updatedAt && (
                    <div className="text-size-12 text-gray-600">
                      data da última modificação: {moment(file.updatedAt).format('DD/MM/YYYY')}
                    </div>
                  )}
                  <div className="text-size-12 text-gray-600">caminho: {file.name}</div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    className="cursor-pointer bg-transparent border-none p-2 hover:bg-gray-200 rounded transition-colors"
                    onClick={() => handleDownload(file)}
                    title="Download"
                  >
                    <img src="/assets/images/downloadFile.svg" alt="Download" className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      )}
    </div>
  );
};

export default DetailFiles;

